/**
 * Revenue Trends — Line/Area charts with interval toggle, margin trends, and approval rates.
 */
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { Activity, Minimize2, Maximize2 } from 'lucide-react';
import api from '@/utils/api';
import { ChartWithDrillDown } from './DrillDownHelpers';

const tooltipStyle = {
  backgroundColor: '#fff', border: '1px solid #e2e8f0',
  borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  fontSize: '13px'
};

const fc = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function TrendCharts({ trendData, trendInterval, setTrendInterval, fetchAnalytics, expandedSections, toggleSection }) {
  const [approvalRates, setApprovalRates] = useState(null);
  const [activeChart, setActiveChart] = useState('revenue');

  useEffect(() => {
    const loadApprovalRates = async () => {
      try {
        const res = await api.get(`/analytics/trends/approval-rates?interval=${trendInterval}&period=last12m`);
        setApprovalRates(res.data.data || []);
      } catch {
        setApprovalRates([]);
      }
    };
    loadApprovalRates();
  }, [trendInterval]);

  if (!trendData?.trend_data?.length) return null;

  const intervals = ['daily', 'weekly', 'monthly', 'quarterly'];
  const charts = [
    { id: 'revenue', label: 'Revenue' },
    { id: 'margins', label: 'Margins' },
    { id: 'volume', label: 'Volume' },
    { id: 'approvals', label: 'Approval Rates' },
  ];
  
  // Column definitions for trend data drill-down
  const trendColumns = [
    { key: 'date', label: 'Period', sortable: true },
    { key: 'proposal_count', label: 'Proposals', sortable: true },
    { key: 'gross_revenue', label: 'Gross Revenue', sortable: true, render: (v) => fc(v) },
    { key: 'net_revenue', label: 'Net Revenue', sortable: true, render: (v) => fc(v) },
    { key: 'merchant_fees', label: 'Merchant Fees', sortable: true, render: (v) => fc(v) },
    { key: 'avg_gpm', label: 'Avg GPM', sortable: true, render: (v) => v ? `${v.toFixed(2)}%` : '-' },
    { key: 'avg_net_margin', label: 'Avg Net Margin', sortable: true, render: (v) => v ? `${v.toFixed(2)}%` : '-' },
  ];

  return (
    <ChartWithDrillDown
      title="Trend Analysis"
      subtitle={`${trendInterval} trends and performance metrics`}
      data={trendData.trend_data}
      columns={trendColumns}
      className=""
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => toggleSection('trends')} className="text-slate-400 hover:text-slate-600">
            {expandedSections.trends ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <Activity className="w-4 h-4 text-sky-600" />
          <h3 className="text-sm font-semibold text-slate-800">Trend Analysis</h3>
        </div>
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-0.5">
          {intervals.map(iv => (
            <button key={iv} onClick={() => { setTrendInterval(iv); fetchAnalytics(); }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all capitalize ${trendInterval === iv ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >{iv}</button>
          ))}
        </div>
      </div>

      {expandedSections.trends && (
        <>
          {/* Chart Type Tabs */}
          <div className="flex gap-1 mb-5 border-b border-slate-100 pb-2">
            {charts.map(c => (
              <button key={c.id} onClick={() => setActiveChart(c.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all ${
                  activeChart === c.id ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >{c.label}</button>
            ))}
          </div>

          {/* Revenue Charts */}
          {activeChart === 'revenue' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Gross vs Net Revenue</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={trendData.trend_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fc(v), name]} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="merchant_fees" fill="#fca5a5" name="Merchant Fees" radius={[2, 2, 0, 0]} />
                    <Line type="monotone" dataKey="gross_revenue" stroke="#0ea5e9" strokeWidth={2} name="Gross Revenue" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="net_revenue" stroke="#10b981" strokeWidth={2} name="Net Revenue" dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Average Revenue per Scenario</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={trendData.trend_data}>
                    <defs>
                      <linearGradient id="avgRevGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fc(v), 'Avg Revenue']} />
                    <Area type="monotone" dataKey="avg_revenue" stroke="#f59e0b" strokeWidth={2} fill="url(#avgRevGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Margin Charts */}
          {activeChart === 'margins' && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">GPM & Net Margin Trends</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData.trend_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v.toFixed(2)}%`, '']} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="avg_gpm" stroke="#0ea5e9" strokeWidth={2.5} name="Avg GPM" dot={{ r: 4, fill: '#0ea5e9' }} />
                  <Line type="monotone" dataKey="avg_net_margin" stroke="#8b5cf6" strokeWidth={2.5} name="Avg Net Margin" dot={{ r: 4, fill: '#8b5cf6' }} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Volume Chart */}
          {activeChart === 'volume' && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Scenario Volume</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trendData.trend_data}>
                  <defs>
                    <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.9} /><stop offset="100%" stopColor="#6366f1" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Scenarios']} />
                  <Bar dataKey="proposal_count" fill="url(#volGrad)" name="Scenario Count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Approval Rate Chart */}
          {activeChart === 'approvals' && approvalRates && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Approval & Rejection Rates</h4>
              {approvalRates.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10">No approval data for the selected period</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={approvalRates}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                    <YAxis yAxisId="left" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                    <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" style={{ fontSize: '11px' }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar yAxisId="left" dataKey="approved" fill="#10b981" name="Approved" radius={[2, 2, 0, 0]} stackId="stack" />
                    <Bar yAxisId="left" dataKey="rejected" fill="#ef4444" name="Rejected" radius={[2, 2, 0, 0]} stackId="stack" />
                    <Bar yAxisId="left" dataKey="pending" fill="#f59e0b" name="Pending" radius={[2, 2, 0, 0]} stackId="stack" />
                    <Line yAxisId="right" type="monotone" dataKey="approval_rate" stroke="#0ea5e9" strokeWidth={2.5} name="Approval %" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Data Points</p>
              <p className="text-lg font-bold text-slate-800 mt-0.5">{trendData.total_data_points}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Interval</p>
              <p className="text-lg font-bold text-slate-800 mt-0.5 capitalize">{trendData.interval}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Gross</p>
              <p className="text-lg font-bold text-sky-600 mt-0.5">{fc(trendData.trend_data.reduce((s, d) => s + (d.gross_revenue || 0), 0))}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Net</p>
              <p className="text-lg font-bold text-emerald-600 mt-0.5">{fc(trendData.trend_data.reduce((s, d) => s + (d.net_revenue || 0), 0))}</p>
            </div>
          </div>
        </>
      )}
    </ChartWithDrillDown>
  );
}
